import { netNewQuestionnaire16167Variant } from '@/services/ab'
import {
  QuestionnaireSection,
  OnboardingIntroPage,
  OnboardingTrialPage,
} from './onboarding.types'
import { trialByPlan15535Treatment } from '@/services/ab/15535_trial_by_plan'

/**
 * Do not import directly. Use it through `onboardingPages` below. We export it for use in tests.
 */
export const introPages: OnboardingIntroPage[] = [
  {
    title: "The <span>world's largest</span> summary library",
    image: 'intro-1.svg',
  },
  {
    title: '<span>Key points with no fluff</span> from nonfiction bestsellers',
    image: 'intro-2.svg',
  },
  {
    title: '<span>Read or listen</span> on desktop, mobile or Kindle',
    image: 'intro-3.svg',
  },
]

/**
 * Do not import directly. Use it through `onboardingPages` below. We export it for use in tests.
 */
export const questionnairePages: QuestionnaireSection[] = [
  {
    title: '_intro',
    id: '_intro',
    pages: [
      {
        id: 'intro',
        type: 'Text',
        title: 'Welcome to Shortform!',
        variableTitle: 'Welcome to Shortform, <span>{{name}}!</span>',
        subtitle: 'Help us personalize your experience!',
        image: {
          src: 'sparkle.png',
          width: 59,
          height: 60,
        },
        textSize: 'large',
        textAlignment: 'center',
      },
    ],
  },
  {
    id: 'about-you',
    title: 'Regarding you',
    pages: [
      {
        id: 'focus',
        type: 'Options',
        title: 'What best describes your goal?',
        options: [
          {
            label: 'Level up my career',
          },
          {
            label: 'Become more thoughtful',
          },
          {
            label: 'Increase my emotional awareness',
          },
          {
            label: 'Improve my career',
          },
          {
            label: 'Learn more about the world',
          },
          {
            label: 'None of the above',
            type: 'text-input',
            placeholder: 'Could you tell us about your goal?',
          },
        ],
      },
      {
        id: 'gender',
        type: 'Options',
        title: 'Please choose your gender',
        wrapInputs: true,
        options: [
          {
            label: 'Male',
          },
          {
            label: 'Female',
          },
          {
            label: 'Other',
          },
          {
            label: 'Prefer not to say',
            type: 'skip',
          },
        ],
      },
      {
        id: 'age',
        type: 'Options',
        title: 'Select your age',
        options: [
          {
            label: '18-25',
          },
          {
            label: '26-45',
          },
          {
            label: '46-55',
          },
          {
            label: '56-65',
          },
          {
            label: '66+',
          },
        ],
      },
    ],
  },
  {
    id: 'aspiration',
    title: 'Goals',
    pages: [
      {
        id: 'goals',
        type: 'Options',
        title: 'What are your main priorities?',
        subtitle:
          'Select up to 3 priorities. They can change as you learn and grow!',
        multipleOptions: true,
        maxOptions: 3,
        options: [
          {
            icon: 'money.svg',
            label: 'Make more money',
          },
          {
            icon: 'trophy.svg',
            label: 'Increase my discipline',
          },
          {
            icon: 'brain.svg',
            label: 'Become smarter',
          },
          {
            icon: 'apple.svg',
            label: 'Live a healthier life',
          },
          {
            icon: 'hearts.svg',
            label: 'Improve my relationships',
          },
          {
            icon: 'balance.svg',
            label: 'Discover inner peace',
          },
          {
            icon: 'bed.svg',
            label: 'Level up my sex life',
          },
          {
            icon: 'kettlebell.svg',
            label: 'Exercise more frequently',
          },
          {
            icon: 'stopwatch.svg',
            label: 'Be more productive',
          },
        ],
      },
      {
        id: 'great-job',
        type: 'Text',
        title: 'Nice work!',
        variableTitle: 'Nice work, {{name}}!',
        subtitle:
          "Here's an achievement for you for being ambitious: the Rising Reader!",
        image: {
          src: 'great-job.svg',
          width: 309,
          height: 364,
        },
        imageAnimation: 'slide-up',
        textSize: 'large',
      },
      {
        id: 'book-summary-question',
        type: 'Options',
        title: 'Have you heard of book guides?',
        centerText: true,
        options: [
          {
            label: 'Yes',
          },
          {
            label: 'No',
          },
          {
            label: "I don't remember",
          },
        ],
      },
      {
        id: 'comparison',
        type: 'Text',
        title:
          'Shortform guides <span>save you time</span> by giving you the key points without fluff',
        image: {
          src: 'comparison.svg',
          width: 406,
          height: 297,
        },
      },
      {
        id: 'audio',
        type: 'Text',
        title:
          'Learn while walking, driving, or shopping, with <span>audio summaries</span>',
        image: {
          src: 'audio.svg',
          width: 412,
          height: 301,
        },
      },
      {
        id: 'daily-goal',
        type: 'DailyGoal',
        title: 'Select your reading pace',
        subtitle: 'This can be adjusted later',
      },
      {
        id: 'commit',
        type: 'Options',
        title: 'Set a growth streak goal',
        image: {
          src: 'growth.svg',
          width: 312,
          height: 156,
        },
        options: [
          {
            label: '1 week',
            hint: 'Trial',
          },
          {
            label: '2 weeks',
            hint: 'Committed',
          },
          {
            label: '1 month',
            hint: 'Focused',
          },
          {
            label: '3 months',
            hint: 'Empowered',
          },
          {
            label: '6 months',
            hint: 'Unstoppable',
          },
        ],
      },
      {
        id: 'reading-when',
        type: 'Options',
        title: 'When do you like to learn something new?',
        options: [
          {
            icon: 'sun.svg',
            label: 'In the morning',
          },
          {
            icon: 'road.svg',
            label: 'On the way to work',
          },
          {
            icon: 'coffee.svg',
            label: 'Eating lunch',
          },
          {
            icon: 'meal.svg',
            label: 'Around dinnertime',
          },
          {
            icon: 'moon.svg',
            label: 'Before bedtime',
          },
          {
            icon: 'relax.svg',
            label: 'General free time',
          },
        ],
      },
      {
        id: 'notifications',
        type: 'Notifications',
        title: 'Turn on notifications to help you with:',
        image: {
          src: 'notifications.svg',
          width: 330,
          height: 204,
        },
        options: [
          {
            title: 'Optimal learning time',
            subtitle:
              'We will send you interesting ideas during times that fit your schedule',
          },
          {
            title: 'Achieving your reading goals',
            subtitle: 'Shortform will remind you to keep your learning going',
          },
          {
            title: 'Reading new content',
            subtitle: "We'll send fresh content that matches your interests",
          },
        ],
      },
      {
        id: 'review',
        type: 'Review',
        title:
          'More than <span>1 million customers trust</span> Shortform guides',
        review:
          "I LOVE Shortform as these are THE BEST summaries I've ever seen … and I've looked at lots of similar sites – like Blinkist and Get Abstracts and Soundview. first time I've ever found smart concise (yet detailed) book summaries that I can read on my phone as I walk on the treadmill",
        reviewer: 'Sophie Moore',
      },
    ],
  },
  {
    id: 'like-time',
    title: 'Preferences',
    pages: [
      {
        id: 'statements',
        type: 'Statements',
        title: 'Do you agree with this quote?',
        hideContinueButton: true,
        content: [
          'I love reading and learning, but I rarely have the time',
          'I care about self-improvement, however, I find it hard to remain disciplined',
          'I want to learn and grow without it being overly difficult',
        ],
      },
      {
        id: 'people-grow',
        type: 'Text',
        title: 'Over <span>1 million</span> happy users!',
        subtitle:
          'Our book guides will help you read faster and hit your learning goals! <span class="source">SimilarWeb, Nov 2024, worldwide</span>',
        image: {
          src: 'world.svg',
          width: 328,
          height: 306,
        },
        textAlignment: 'left',
      },
      {
        id: 'books',
        type: 'Books',
        title: 'Does this book interest you?',
        hideContinueButton: true,
        content: [
          'atomic-habits',
          'the-48-laws-of-power',
          'the-7-habits-of-highly-effective-people',
          'how-to-win-friends-and-influence-people',
          'deep-work',
        ],
      },
    ],
  },
  {
    id: '_outro',
    title: 'Extra',
    pages: [
      {
        id: 'finishing-setup',
        type: 'FinishingSetup',
        title: 'Your <span>reading plan</span> is ready!',
        titleLoading: 'Personalizing your <span>reading plan...</span>',
        hideContinueButton: true,
        steps: [
          {
            title: 'Growth targets',
            titleLoading: 'Analyzing growth targets',
            question: 'Do you get distracted easily?',
          },
          {
            title: 'Learning style',
            titleLoading: 'Setting learning style',
            question: 'Do you follow through with your plans?',
          },
          {
            title: 'Reading list',
            titleLoading: 'Curating reading list',
            question: 'Do you like to learn new things?',
          },
        ],
      },
      {
        id: 'commitment-pact',
        type: 'CommitmentPact',
        title: 'Growth Pledge',
        textInitial:
          "I must continue to learn and improve as a person in order to achieve my goals<br>That's why I am pledging to make reading a core part of my life.<br>I believe that Shortform will help me on my path to becoming a wiser and better person.",
        textFinal:
          "Hi, it's you from the future. It's an exciting day for you!<br>On {{currentDate}}, we pledged to begin a journey of learning & growth.<br>Now, in the future, I am wiser and live a more fulfilling life. I appreciate you for committing to the path of self improvement.",
        hideContinueButton: true,
      },
    ],
  },
]

/**
 * Do not import directly. Use it through `onboardingPages` below. We export it for use in tests.
 */
export const questionnairePages16167: QuestionnaireSection[] = [
  {
    id: '_intro',
    title: '_intro',
    pages: [
      {
        id: 'welcome',
        type: 'Welcome16167',
        title: "The world's highest-quality summaries",
        introTitle: 'Welcome to Shortform',
        subtitle: '<span>Over 15,000 books</span> across dozens of categories',
        hideContinueButton: true,
        image: {
          src: 'mascot-welcome.svg',
          width: 405,
          height: 422,
        },
      },
      {
        id: 'save-8-hours',
        type: 'Text',
        title: 'Save <span>8 hours</span> a week',
        subtitle:
          'if you read 4 books a month in Shortform instead of the original versions.',
        animation: {
          src: 'save-8-hours.riv',
          width: 375,
          height: 375,
          stateMachines: 'State Machine 1',
        },
        textSize: 'large',
        showLogo: true,
      },
    ],
  },
  {
    id: 'introduction',
    title: 'Introduction',
    pages: [
      {
        id: 'atomic-habits-splitscreen',
        type: 'Splitscreen16167',
        title: 'Shortform Guide vs Original Book',
        topText: ['8 hours', 'Read only', 'Padded length'],
        bottomText: ['50 minutes', 'Read and listen', '1-page summary'],
        animation: {
          src: 'atomic-habits-splitscreen.riv',
          width: 500,
          height: 500,
        },
        hideContinueButton: true,
      },
      {
        id: 'upgrade',
        type: 'Upgrade16167',
        title: 'Upgrade Your',
        sectionTitles: ['Self', 'Career', 'Relationships'],
        sectionSubtitles: [
          'Productivity, Mindset, Health',
          'Leadership, Strategy, Entrepreneurship',
          'Relationship Psychology, Communication, Parenting',
        ],
        animation: {
          src: 'upgrade.riv',
          width: 342,
          height: 310,
        },
        hideContinueButton: true,
      },
      {
        id: '1-million-users',
        type: 'MillionUsers16167',
        title: '<span>Over 1 million users</span>',
        subtitle:
          'Bestselling authors, top-tier CEOs, and leading academics trust Shortform summaries',
        content: [
          {
            name: 'Ali Abdaal',
            subtitle: 'Productivity Youtuber,<br>500 million views',
            image: 'testimonial-ali-abdaal.webp',
            text: "I've been using Shortform for years, it's by far the best non-fiction book summary service out there. If you like books, learning and doing those things in a time efficient way then you should definitely check out Shortform.",
          },
          {
            name: 'Mark Manson',
            subtitle:
              '3-time #1 New York Times Bestseller, Author of "The Subtle Art of Not Giving a F*ck"',
            image: 'testimonial-mark-manson.webp',
            text: 'I’ve found Shortform to be a fantastic supplement to my research and general intellectual curiosity. I wrote four of the books they summarized, and they f*cking nailed it.',
          },
          {
            name: 'Sahil Bloom',
            subtitle:
              'Pulitzer Prize Finalist, Creator of the newsletter "The Curiosity Chronicle"',
            image: 'testimonial-sahil-bloom.webp',
            text: 'If you’re like me and always on the hunt for ways to read more nonfiction books and boost your productivity, I highly recommend checking out Shortform. It’s become my secret weapon for absorbing the most important insights from the world’s top nonfiction books.',
          },
        ],
        animation: {
          src: '1-million-users.riv',
          width: 600,
          height: 600,
        },
        hideContinueButton: true,
      },
      {
        id: 'product-demo',
        type: 'ProductDemo16167',
        title: 'Why are our summaries the highest-quality?',
        content: [
          'Hyper-focused on giving useful info to help you achieve your goals',
          '1-page summaries: get actionable insights fast',
          'Chapter summaries: go deeper and efficiently learn details that matter',
          'Expert commentary: outside context & alternate viewpoints',
          'Audio narration: fully voiced book summaries',
        ],
        animationMobile: {
          src: 'product-demo-mobile.riv',
          width: 324,
          height: 696,
        },
        animationDesktop: {
          src: 'product-demo-desktop.riv',
          width: 792,
          height: 482,
        },
        hideContinueButton: true,
      },
    ],
  },
  {
    id: 'goals',
    title: 'Your goals',
    pages: [
      {
        id: 'personalize',
        type: 'Text',
        title:
          'Personalize your Shortform experience in <span>2 minutes</span>',
        subtitle:
          "We'll recommend content to help you quickly achieve your learning goals.",
        animation: {
          src: 'personalize.riv',
          width: 375,
          height: 375,
        },
        textSize: 'large',
      },
      {
        id: 'what-prevents-you',
        type: 'Options',
        title: 'What prevents you from reading more?',
        subtitle: 'Select all that apply',
        multipleOptions: true,
        options: [
          {
            icon: 'hourglass-emoji.png',
            label: 'My schedule is a circus',
          },
          {
            icon: 'sleepy-emoji.png',
            label: 'Reading puts me to sleep...',
          },
          {
            icon: 'magnifying-emoji.png',
            label: 'Nothing grabs my attention',
          },
          {
            icon: 'headphones-emoji.png',
            label: 'I prefer other forms of media',
          },
          {
            icon: 'cool-emoji.png',
            label: "I'm already a reading legend",
          },
        ],
      },
      {
        id: 'genre-selection',
        type: 'GenreSelection16167',
        title: 'Where do you want to improve your life?',
        subtitle: 'Select all that apply.',
        options: [
          {
            id: 'communication',
            icon: 'communication16167.svg',
            label: 'Communication',
            color: '#7DB3FC',
          },
          {
            id: 'relationships',
            icon: 'relationships16167.svg',
            label: 'Relationships',
            color: '#E75A33',
          },
          {
            id: 'productivity',
            icon: 'productivity16167.svg',
            label: 'Productivity',
            color: '#FFBB25',
          },
          {
            id: 'business',
            icon: 'business16167.svg',
            label: 'Business',
            color: '#DBA0F8',
          },
          {
            id: 'leadership',
            icon: 'leadership16167.svg',
            label: 'Leadership',
            color: '#F8B3B8',
          },
          {
            id: 'health',
            icon: 'health16167.svg',
            label: 'Health & Wellness',
            color: '#73B76D',
          },
        ],
      },
      {
        id: 'book-selection',
        type: 'BookSelection16167',
        title: 'What book summaries would you like to read first?',
        hideContinueButton: true,
      },
      {
        id: 'building-reading-list',
        type: 'Loading16167',
        title: 'Building your first reading list...',
        titleDone: 'Find your first reading list in My Library',
        titleError: 'Something went wrong',
        animation: {
          src: 'building-reading-list.riv',
          width: 300,
          height: 377,
        },
        hideContinueButton: true,
      },
      {
        id: 'summary',
        type: 'Summary16167',
        title: 'Questionnaire Summary',
      },
    ],
  },
]

/**
 * Do not import directly. Use it through `onboardingPages` below. We export it for use in tests.
 */
export const trialPages: OnboardingTrialPage[] = [
  {
    title: 'How our Free Trial works',
    steps: [
      {
        text: 'Free Premium access to Shortform. No payment now',
        icon: 'lock.svg',
        day: 'Today',
      },
      {
        text: 'We will remind you before your trial ends',
        icon: 'bell.svg',
        day: 'Day 3',
      },
      {
        text: trialByPlan15535Treatment()
          ? 'Cancel in advance to avoid payment'
          : 'Cancel your subscription to avoid a charge',
        icon: 'coin.svg',
        day: 'Day 5',
      },
    ],
  },
  {
    title:
      'We’ll notify you <span>2 days</span> before your free trial is over.',
    image: {
      src: 'trial.svg',
      width: 360,
      height: 220,
    },
  },
]

export const onboardingPages = {
  get intro(): OnboardingIntroPage[] {
    return introPages
  },
  get questionnaire(): QuestionnaireSection[] {
    if (netNewQuestionnaire16167Variant() === 'B') {
      return questionnairePages16167
    }
    return questionnairePages
  },
  get trial(): OnboardingTrialPage[] {
    return trialPages
  },
}
